import { RouterState } from 'connected-react-router';
import { isProperValue } from '../type/typeUtil';

export interface PartialLocation {
    hostname: string;
    protocol: string;
    port: string;
}

export const getRootUrlFromLocation = (location: PartialLocation): string => {
    const { hostname, protocol, port } = location;
    let methodAndHost = `${protocol}//${hostname}`;

    if (port !== '80' && port !== '443' && port !== '') {
        methodAndHost += `:${port}`;
    }
    return methodAndHost;
};

export const getCurrentProtocolHostAndPort = (): string => {
    return getRootUrlFromLocation(window.location);
};

export const prependCurrentProtocolHostAndPort = (path: string): string => `${getCurrentProtocolHostAndPort()}${path}`;

export const getWorkspaceIdFromPathname = (routerState: RouterState): string => (
    routerState?.location?.pathname.substring(1).split('/')[0]
);

export const getUrlParam = (paramName: string, search = window.location.search): string => {
    const param = search
        .split(/\?|&/)
        .find((item) => item.split('=')[0] === paramName);

    return param ? param.split('=')[1] : undefined;
};

export const parseLocationSearchString = (search: string): { [index: string]: any } => {
    return search
        .substring(search.indexOf('?') + 1)
        .split('&')
        .map((part) => part.split('=').map((str) => str.trim()))
        .map((keyVal): { [index: string]: any } => {
            const [key, value] = keyVal;
            const retVal: { [index: string]: any } = { [key]: value };

            if (value === undefined) {
                return {};
            } if (value.toLowerCase() === 'true') {
                retVal[key] = true;
            } else if (value.toLowerCase() === 'false') {
                retVal[key] = false;
            } else if (value.includes(',')) {
                retVal[key] = value
                    .split(',')
                    .map((val) => parseInt(val, 10));
            } else if (!Number.isNaN(parseInt(value, 10))) {
                retVal[key] = parseInt(value, 10);
            }

            return retVal;
        })
        .reduce(
            (accumulator, keyVal) => {
                return {
                    ...accumulator,
                    ...keyVal,
                };
            },
            {},
        );
};

export const buildUrlQueryString = (props: { [index: string]: any }): string => {
    const parts = Object
        .entries(props)
        .filter((keyVal) => isProperValue(keyVal[1]))
        .map((keyVal) => {
            if (Array.isArray(keyVal[1])) {
                keyVal[1] = keyVal[1].join(',');
            }
            return keyVal;
        })
        .map((keyVal) => `${keyVal[0]}=${keyVal[1]}`)
        .join('&');

    return `?${parts}`;
};

export const removeCurrentUrlParam = (paramName: string, search = window.location.search): void => {
    const param = search
        .split(/\?|&/)
        .find((item) => item.split('=')[0] === paramName);

    const newParams = param ? search.replace(param, '') : undefined;

    if (newParams) {
        window.history.replaceState({}, document.title, `${newParams}`);
    }
};

export const generateLinkForAuthorCall = (parameters: any): string => {
    const params = urlUtil.parseLocationSearchString(window.location.search);
    const validReviewWorkspaceIds = ['item_bank_default', 'item_bank_default_question'];

    if (params.workspaceId) {
        delete params.workspaceId;
    }
    if (parameters.workspaceId === 'author_preview') {
        params.isPreviewMode = true;
        delete params.isReviewMode;
    } else if (validReviewWorkspaceIds.includes(parameters.workspaceId)
               || (params.oneWorkspaceMode && parameters.workspaceId === 'item_review_xtm')) {
        params.isReviewMode = true;
        delete params.isPreviewMode;
    } else {
        delete params.isPreviewMode;
        delete params.isReviewMode;
    }

    const queryString: string = urlUtil.buildUrlQueryString(params);
    return `/${parameters.workspaceId}/${queryString}`;
};

export const getAssertUrl = (contentItemId: number): string => {
    const params = {
        searchText: contentItemId,
        columns: [
            { name: 'title' },
            { name: 'objectId' },
            { name: 'contentType' },
            { name: 'lastModified' },
            { name: 'mimeType' },
            { name: 'fileSize' },
            { name: 'sourceFileName' },
        ],
    };
    return `/admin#itemBank/assets/data/${encodeURIComponent(JSON.stringify(params))}`;
};

const urlUtil = {
    buildUrlQueryString,
    getCurrentProtocolHostAndPort,
    getRootUrlFromLocation,
    getUrlParam,
    parseLocationSearchString,
    prependCurrentProtocolHostAndPort,
    generateLinkForAuthorCall,
    getWorkspaceIdFromPathname,
    getAssertUrl,
};

export default urlUtil;
